import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";

export const ConfirmDialog = forwardRef(
  (
    {
      onContinue,
      onCancel,
      onClose,
      title,
      message,
      children,
      className,
      contentClassName,
      continueLabel,
      continueVariant,
      cancelLabel,
      cancel,
      continueIcon,
      cancelIcon,
      open: initiallyOpen,
      closeOnEscape = true,
      autoFocus = true
    }: {
      onContinue?: () => void;
      onCancel?: () => void;
      onClose?: () => void;
      title?: string;
      message?: React.ReactNode;
      children?: React.ReactNode;
      className?: string;
      contentClassName?: string;
      continueLabel?: string;
      continueVariant?: "default" | "destructive";
      cancelLabel?: string;
      cancel?: boolean;
      continueIcon?: React.ReactNode;
      cancelIcon?: React.ReactNode;
      open?: boolean;
      closeOnEscape?: boolean;
      autoFocus?: boolean;
    },
    ref: React.ForwardedRef<{
      open: () => void;
      close: () => void;
    }>
  ) => {
    const [open, setOpen] = useState(initiallyOpen ?? false);

    useEffect(() => {
      setOpen(initiallyOpen ?? false);
    }, [initiallyOpen]);

    useImperativeHandle(ref, () => ({
      open: () => {
        setOpen(true);
      },
      close: () => {
        setOpen(false);
      }
    }));

    return (
      <AlertDialog
        open={open}
        onOpenChange={newOpen => {
          setOpen(newOpen);
          if (!newOpen && onClose) {
            onClose();
          }
        }}
      >
        <AlertDialogContent
          className={contentClassName}
          onEscapeKeyDown={
            closeOnEscape
              ? undefined
              : e => {
                  e.preventDefault();
                }
          }
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <AlertDialogDescription className={className}>
              {typeof message === "string" ? (
                <div className="text-md font-bold my-4">{message}</div>
              ) : message !== undefined && message !== null ? (
                message
              ) : null}
              {children}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {(cancel ?? true) ? (
              <AlertDialogCancel
                onClick={e => {
                  e.preventDefault();
                  setOpen(false);
                  onCancel && onCancel();
                }}
              >
                {cancelIcon}
                {cancelLabel ?? "Cancel"}
              </AlertDialogCancel>
            ) : null}

            <AlertDialogAction
              autoFocus={autoFocus}
              disabled={!onContinue}
              className={cn(
                continueVariant === "destructive" &&
                  "bg-destructive text-destructive-foreground hover:bg-destructive/90"
              )}
              onKeyDown={e => {
                e.preventDefault();

                if (e.key === "Enter" && onContinue) {
                  setOpen(false);
                  onContinue();
                }
              }}
              onClick={e => {
                e.preventDefault();

                if (onContinue) {
                  setOpen(false);
                  onContinue();
                }
              }}
            >
              {continueIcon}
              {continueLabel ?? "Continue"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }
);

ConfirmDialog.displayName = "ConfirmDialog";
